import { Button } from '@cotera/client/v0/components/ui/button';
import { cn } from '@cotera/client/v0/lib/utils';
import { ArrowDownIcon } from 'lucide-react';
import type { ComponentProps, MutableRefObject } from 'react';
import { useCallback, useEffect } from 'react';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';

export type ConversationProps = ComponentProps<typeof StickToBottom>;

export const Conversation = ({ className, ...props }: ConversationProps) => (
  <StickToBottom
    className={cn(
      'relative flex-1 min-h-0 overflow-y-auto overscroll-y-none [&>div]:overscroll-y-none',
      className
    )}
    initial="smooth"
    resize="smooth"
    role="log"
    {...props}
  />
);

export type ConversationContentProps = ComponentProps<
  typeof StickToBottom.Content
>;

/**
 * How long the bar stays up after the last scroll event. Long enough to follow a
 * flick to its end, short enough that it is gone by the time you start reading
 * where you landed.
 */
const SCROLL_SETTLE_MS = 700;

/**
 * Flags the scroll element with `data-scrolling` while it is scrolling, which is
 * what lets `overlay-scrollbar` show a bar only then.
 *
 * It takes JavaScript because the browser will not tell us in CSS: macOS fades
 * its own overlay bars in and out on exactly this signal, but a page that styles
 * `::-webkit-scrollbar` anywhere gives that up, and this app styles it globally.
 * `:hover` is the usual stand-in and is the wrong signal here — the pointer sits
 * over the conversation the whole time you are reading it, so the bar would be
 * up permanently for anyone using a mouse.
 */
const useScrollActivity = (
  scrollRef: MutableRefObject<HTMLElement | null>
): void => {
  useEffect(() => {
    const element = scrollRef.current;
    if (element === null) {
      return;
    }
    let settleTimeout: number | undefined;
    const handleScroll = (): void => {
      element.dataset['scrolling'] = '';
      if (settleTimeout !== undefined) {
        window.clearTimeout(settleTimeout);
      }
      settleTimeout = window.setTimeout(() => {
        delete element.dataset['scrolling'];
      }, SCROLL_SETTLE_MS);
    };
    element.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      element.removeEventListener('scroll', handleScroll);
      if (settleTimeout !== undefined) {
        window.clearTimeout(settleTimeout);
      }
    };
  }, [scrollRef]);
};

export const ConversationContent = ({
  className,
  scrollClassName,
  ...props
}: ConversationContentProps) => {
  // `StickToBottom` renders the element that actually scrolls one level inside
  // `Conversation`, so this is the only place with a handle on it.
  const { scrollRef } = useStickToBottomContext();
  useScrollActivity(scrollRef);

  return (
    <StickToBottom.Content
      className={cn('p-4 max-w-5xl mx-auto', className)}
      scrollClassName={cn('overlay-scrollbar', scrollClassName)}
      {...props}
    />
  );
};

export type ConversationEmptyStateProps = ComponentProps<'div'> & {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
};

export const ConversationEmptyState = ({
  className,
  title = 'No messages yet',
  description = 'Start a conversation to see messages here',
  icon,
  children,
  ...props
}: ConversationEmptyStateProps) => (
  <div
    className={cn(
      'flex size-full flex-col items-center justify-center gap-3 p-8 text-center',
      className
    )}
    {...props}
  >
    {children ?? (
      <>
        {icon !== undefined && (
          <div className="text-muted-foreground">{icon}</div>
        )}
        <div className="space-y-1">
          <h3 className="font-medium text-sm">{title}</h3>
          {description && (
            <p className="text-muted-foreground text-sm">{description}</p>
          )}
        </div>
      </>
    )}
  </div>
);

export type ConversationScrollButtonProps = ComponentProps<typeof Button>;

export const ConversationScrollButton = ({
  className,
  ...props
}: ConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  const handleScrollToBottom = useCallback(async () => {
    await scrollToBottom();
  }, [scrollToBottom]);

  return (
    !isAtBottom && (
      <Button
        className={cn(
          'absolute bottom-4 left-[50%] translate-x-[-50%] rounded-full',
          className
        )}
        onClick={handleScrollToBottom}
        size="icon"
        type="button"
        variant="outline"
        {...props}
      >
        <ArrowDownIcon className="size-4" />
      </Button>
    )
  );
};
